import type {
  SmartAttributionAssignment,
  SmartAttributionCluster,
  SmartAttributionCoordinate,
  SmartAttributionCoproInput,
  SmartAttributionParams,
} from './types';
import { DEBUG_DEFAULTS } from './types';

const toRadians = (value: number) => (value * Math.PI) / 180;

const withFallback = (coordinate?: SmartAttributionCoordinate | null) => {
  if (!coordinate) {
    return null;
  }
  const { lat, long } = coordinate;
  if (!Number.isFinite(lat) || !Number.isFinite(long)) {
    return null;
  }
  return { lat, long } as SmartAttributionCoordinate;
};

// Helper functions and interfaces (defined outside main function)
const cloneAssignment = (copro: SmartAttributionCoproInput): SmartAttributionAssignment => ({
  coproId: copro.id,
  coproName: copro.name,
  salesAmount: copro.salesAmount,
  lat: copro.lat ?? null,
  long: copro.long ?? null,
});

const hasLatLng = (copro: SmartAttributionCoproInput): copro is SmartAttributionCoproInput & Required<Pick<SmartAttributionCoproInput, 'lat' | 'long'>> =>
  Number.isFinite(copro.lat) && Number.isFinite(copro.long);

interface ClusterInternalState {
  userId: number;
  targetPercentage: number;
  targetSalesAmount: number;
  assignedSalesAmount: number;
  assignments: SmartAttributionAssignment[];
  anchor: SmartAttributionCoordinate | null;
}


export function computeSmartAttribution({ copros, targets, allowAnchorsFallback = true, debugParams }: SmartAttributionParams) {
  const effectiveDebugParams = debugParams ?? DEBUG_DEFAULTS;

  const earthRadiusKm = effectiveDebugParams.earthRadiusKm ?? DEBUG_DEFAULTS.earthRadiusKm;
  const epsilon = effectiveDebugParams.epsilon ?? DEBUG_DEFAULTS.epsilon;
  const capacityTolerance = effectiveDebugParams.capacityTolerance ?? DEBUG_DEFAULTS.capacityTolerance;
  const knnK = effectiveDebugParams.knnK ?? DEBUG_DEFAULTS.knnK;
  const knnMaxDistanceKm = effectiveDebugParams.knnMaxDistanceKm ?? DEBUG_DEFAULTS.knnMaxDistanceKm;
  const knnDistanceWeight = effectiveDebugParams.knnDistanceWeight ?? DEBUG_DEFAULTS.knnDistanceWeight;
  const knnCapacityWeight = effectiveDebugParams.knnCapacityWeight ?? DEBUG_DEFAULTS.knnCapacityWeight;
  const distancePriorityDivider = effectiveDebugParams.distancePriorityDivider ?? DEBUG_DEFAULTS.distancePriorityDivider;
  const maxAssignmentPasses = effectiveDebugParams.maxAssignmentPasses ?? DEBUG_DEFAULTS.maxAssignmentPasses;
  const maxRebalanceIterations = effectiveDebugParams.maxRebalanceIterations ?? DEBUG_DEFAULTS.maxRebalanceIterations;
  const distanceReassignPenalty = effectiveDebugParams.distanceReassignPenalty ?? DEBUG_DEFAULTS.distanceReassignPenalty;
  const rebalanceNeighborCount = effectiveDebugParams.rebalanceNeighborCount ?? DEBUG_DEFAULTS.rebalanceNeighborCount;
  const rebalanceMaxDistanceKm = effectiveDebugParams.rebalanceMaxDistanceKm ?? DEBUG_DEFAULTS.rebalanceMaxDistanceKm;
  const rebalanceDistanceGainWeight = effectiveDebugParams.rebalanceDistanceGainWeight ?? DEBUG_DEFAULTS.rebalanceDistanceGainWeight;

  // Helper functions defined inside main function to access parameters
  const haversineDistanceKm = (a: SmartAttributionCoordinate, b: SmartAttributionCoordinate) => {
    const dLat = toRadians(b.lat - a.lat);
    const dLon = toRadians(b.long - a.long);
    const lat1 = toRadians(a.lat);
    const lat2 = toRadians(b.lat);

    const sinLat = Math.sin(dLat / 2);
    const sinLon = Math.sin(dLon / 2);
    const haversine = (sinLat ** 2) + ((sinLon ** 2) * Math.cos(lat1) * Math.cos(lat2));
    const c = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

    return earthRadiusKm * c;
  };

  const ratio = (cluster: ClusterInternalState) => {
    if (cluster.targetSalesAmount <= 0) {
      return 0;
    }
    const targetBuffer = cluster.targetSalesAmount + epsilon;
    return cluster.assignedSalesAmount / targetBuffer;
  };

  const buildClusters = (targetsParam: SmartAttributionParams['targets'], totalSalesAmountParam: number): ClusterInternalState[] =>
    targetsParam.map((target) => ({
      userId: target.userId,
      targetPercentage: target.percentage,
      targetSalesAmount: (totalSalesAmountParam * target.percentage) / 100,
      assignedSalesAmount: 0,
      assignments: [],
      anchor: withFallback(target.anchor ?? null),
    }));

  const assign = (cluster: ClusterInternalState, copro: SmartAttributionCoproInput) => {
    cluster.assignments.push(cloneAssignment(copro));
    cluster.assignedSalesAmount += copro.salesAmount;
  };

  const computeDistanceToCluster = (cluster: ClusterInternalState, point: { lat: number | null | undefined; long: number | null | undefined }) => {
    if (!cluster.anchor || point.lat == null || point.long == null) {
      return Number.POSITIVE_INFINITY;
    }
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.long)) {
      return Number.POSITIVE_INFINITY;
    }
    return haversineDistanceKm(cluster.anchor, { lat: point.lat, long: point.long });
  };

  const computeAffinityScore = (cluster: ClusterInternalState, copro: SmartAttributionCoproInput, allClusters: ClusterInternalState[]) => {
    const distanceKm = computeDistanceToCluster(cluster, {
      lat: copro.lat ?? null,
      long: copro.long ?? null,
    });

    if (!Number.isFinite(distanceKm)) {
      return -Infinity;
    }

    // Find K nearest neighbors for KNN scoring
    const neighborsWithDistances = allClusters
      .filter(c => c !== cluster)
      .map(c => ({
        cluster: c,
        distance: computeDistanceToCluster(c, {
          lat: copro.lat ?? null,
          long: copro.long ?? null,
        }),
      }))
      .filter(entry => Number.isFinite(entry.distance) && entry.distance <= knnMaxDistanceKm)
      .sort((a, b) => (a.distance as number) - (b.distance as number))
      .slice(0, knnK);

    // KNN-based proximity score: higher score for being close to neighbors
    const proximityScore = neighborsWithDistances.length > 0
      ? neighborsWithDistances.reduce((acc, neighbor) => {
          const normalizedDistance = Math.max(neighbor.distance as number, 0.1) / knnMaxDistanceKm;
          return acc + (1 - normalizedDistance);
        }, 0) / neighborsWithDistances.length
      : 0;

    // Distance score: higher for closer clusters
    const distanceScore = distanceKm <= knnMaxDistanceKm
      ? (knnMaxDistanceKm - distanceKm) / knnMaxDistanceKm
      : 0;

    // Capacity balance score - minimal penalty for over-capacity to heavily prioritize distance
    const deficit = cluster.targetSalesAmount - cluster.assignedSalesAmount;
    const targetBuffer = cluster.targetSalesAmount + epsilon;
    const capacityScore = cluster.targetSalesAmount > 0
      ? (deficit >= 0
        ? Math.min(deficit / targetBuffer, 0.6) // Favor clusters that need more capacity
        : Math.max(0.4, 1 - (Math.abs(deficit) / (targetBuffer * 4)))) // Very minimal penalty for over-capacity to allow coverage gaps
      : 0.5;

    const anchorPenalty = cluster.anchor ? 0 : 0.1; // Reduced anchor penalty

    // Combined KNN score: much stronger weight on direct distance vs proximity
    const distanceProximityScore = (proximityScore + (distanceScore * 3)) / 4; // Prioritize direct distance heavily
    const knnScore = (knnDistanceWeight * distanceProximityScore) +
                     (knnCapacityWeight * capacityScore);

    return knnScore - anchorPenalty;
  };

  const findClusterWithLowestRatio = (clusters: ClusterInternalState[]) => clusters.reduce<ClusterInternalState | null>((best, candidate) => {
    if (!best) {
      return candidate;
    }
    return ratio(candidate) < ratio(best) ? candidate : best;
  }, null);

  const findClusterWithHighestRatio = (clusters: ClusterInternalState[]) => clusters.reduce<ClusterInternalState | null>((best, candidate) => {
    if (!best) {
      return candidate;
    }
    return ratio(candidate) > ratio(best) ? candidate : best;
  }, null);

  const fallbackClusterCoordinates = (clusters: ClusterInternalState[], coprosParam: SmartAttributionCoproInput[]) => {
    const availableCopros = coprosParam.filter((copro) => Number.isFinite(copro.lat) && Number.isFinite(copro.long));
    if (availableCopros.length === 0) {
      return;
    }

    clusters.forEach((cluster, index) => {
      if (!cluster.anchor) {
        const fallbackCopro = availableCopros[index % availableCopros.length];
        if (fallbackCopro.lat != null && fallbackCopro.long != null) {
          cluster.anchor = { lat: fallbackCopro.lat, long: fallbackCopro.long };
        }
      }
    });
  };

  const initialAssignment = (clusters: ClusterInternalState[], geolocatedCopros: SmartAttributionCoproInput[]) => {
    if (clusters.length === 0) {
      return;
    }

    const remaining = [...geolocatedCopros];
    let passes = 0;

    while (remaining.length > 0 && passes < maxAssignmentPasses) {
      passes += 1;
      let progress = false;

      for (let index = remaining.length - 1; index >= 0; index -= 1) {
        const copro = remaining[index];
        let bestCluster: ClusterInternalState | null = null;
        let bestScore = -Infinity;

        clusters.forEach((cluster) => {
          const score = computeAffinityScore(cluster, copro, clusters);
          if (score > bestScore) {
            bestScore = score;
            bestCluster = cluster;
          }
        });

        if (bestCluster) {
          assign(bestCluster, copro);
          remaining.splice(index, 1);
          progress = true;
        }
      }

      if (!progress) {
        break;
      }
    }

    remaining.forEach((copro) => {
      const targetCluster = findClusterWithLowestRatio(clusters);
      if (targetCluster) {
        assign(targetCluster, copro);
      }
    });
  };

  const assignOrphanCopros = (clusters: ClusterInternalState[], orphanCopros: SmartAttributionCoproInput[]) => {
    orphanCopros.forEach((copro) => {
      const targetCluster = findClusterWithLowestRatio(clusters);
      if (targetCluster) {
        assign(targetCluster, copro);
      }
    });
  };

  const computeDeviationForClusters = (clusterA: ClusterInternalState, clusterB: ClusterInternalState, deltaA: number, deltaB: number) => {
    const diffA = clusterA.assignedSalesAmount - clusterA.targetSalesAmount;
    const diffB = clusterB.assignedSalesAmount - clusterB.targetSalesAmount;

    const newDiffA = diffA + deltaA;
    const newDiffB = diffB + deltaB;

    const currentDeviation = Math.abs(diffA) + Math.abs(diffB);
    const newDeviation = Math.abs(newDiffA) + Math.abs(newDiffB);

    return { currentDeviation, newDeviation };
  };

  const getNeighborClusters = (clusters: ClusterInternalState[], origin: ClusterInternalState) => clusters
    .filter((cluster) => cluster !== origin)
    .map((cluster) => ({ cluster, distance: origin.anchor && cluster.anchor ? haversineDistanceKm(origin.anchor, cluster.anchor) : Number.POSITIVE_INFINITY }))
    .sort((a, b) => (a.distance - b.distance))
    .slice(0, Math.max(knnK, rebalanceNeighborCount))
    .filter((entry) => entry.distance <= Math.min(knnMaxDistanceKm, rebalanceMaxDistanceKm));

  const attemptRebalance = (clusters: ClusterInternalState[]) => {
    if (clusters.length < 2) {
      return;
    }

    for (let attempt = 0; attempt < maxRebalanceIterations; attempt += 1) {
      const mostLoaded = findClusterWithHighestRatio(clusters);
      const leastLoaded = findClusterWithLowestRatio(clusters);

      if (!mostLoaded || !leastLoaded) {
        break;
      }

      const overloaded = ratio(mostLoaded) > (1 + capacityTolerance);
      const underloaded = ratio(leastLoaded) < (1 - capacityTolerance);

      if (!overloaded && !underloaded) {
        break;
      }

      let bestScore = 0;
      let bestMove: {
        from: ClusterInternalState;
        to: ClusterInternalState;
        assignmentIndex: number;
        assignment: SmartAttributionAssignment;
      } | null = null;

      clusters.forEach((fromCluster) => {
        if (ratio(fromCluster) <= 1 || fromCluster.assignments.length === 0) {
          return;
        }

        const neighbors = getNeighborClusters(clusters, fromCluster);
        if (neighbors.length === 0) {
          return;
        }

        fromCluster.assignments.forEach((assignment, assignmentIndex) => {
          const currentDistance = computeDistanceToCluster(fromCluster, assignment);

          neighbors.forEach(({ cluster: toCluster, distance: clusterDistance }) => {
            const nextDistance = computeDistanceToCluster(toCluster, assignment);

            if (!Number.isFinite(nextDistance) || !Number.isFinite(currentDistance)) {
              return;
            }

            const distanceImprovement = (currentDistance as number) - (nextDistance as number);

            if (distanceImprovement <= 0 && clusterDistance > rebalanceMaxDistanceKm) {
              return;
            }

            const { currentDeviation, newDeviation } = computeDeviationForClusters(
              fromCluster,
              toCluster,
              -assignment.salesAmount,
              assignment.salesAmount,
            );

            const improvement = currentDeviation - newDeviation;
            if (improvement <= 0 && distanceImprovement <= 0) {
              return;
            }

            const projectedTargetBuffer = toCluster.targetSalesAmount + epsilon;
            const projectedRatio = toCluster.targetSalesAmount > 0
              ? (toCluster.assignedSalesAmount + assignment.salesAmount) / projectedTargetBuffer
              : Number.POSITIVE_INFINITY;

            const ratioPenalty = Math.max(projectedRatio - (1 + capacityTolerance), 0) * distanceReassignPenalty;

            const positiveDistanceImprovement = Math.max(distanceImprovement, 0);
            const normalizedDistanceImprovement = positiveDistanceImprovement / Math.max(distancePriorityDivider, 1);
            const knnBoost = rebalanceDistanceGainWeight > 0
              ? normalizedDistanceImprovement * rebalanceDistanceGainWeight
              : 0;

            const score = improvement - ratioPenalty + knnBoost;

            if (score > bestScore + epsilon) {
              bestScore = score;
              bestMove = {
                from: fromCluster,
                to: toCluster,
                assignmentIndex,
                assignment,
              };
            }
          });
        });
      });

      if (!bestMove) {
        break;
      }

      const { from, to, assignment, assignmentIndex } = bestMove;
      from.assignments.splice(assignmentIndex, 1);
      from.assignedSalesAmount -= assignment.salesAmount;
      to.assignments.push(assignment);
      to.assignedSalesAmount += assignment.salesAmount;
    }
  };

  const propagateAssignments = (clusters: ClusterInternalState[], coprosParam: SmartAttributionCoproInput[]) => {
    const geolocatedCopros = coprosParam.filter(hasLatLng);
    const orphanCopros = coprosParam.filter((copro) => !hasLatLng(copro));

    initialAssignment(clusters, geolocatedCopros);
    assignOrphanCopros(clusters, orphanCopros);
    attemptRebalance(clusters);
  };

  const computeCoverage = (clusters: ClusterInternalState[], totalSalesAmountParam: number) => clusters.map<SmartAttributionCluster>((cluster) => ({
    userId: cluster.userId,
    targetPercentage: cluster.targetPercentage,
    targetSalesAmount: cluster.targetSalesAmount,
    assignedSalesAmount: cluster.assignedSalesAmount,
    assignments: cluster.assignments,
    coveragePercentage: totalSalesAmountParam > 0
      ? (cluster.assignedSalesAmount / totalSalesAmountParam) * 100
      : 0,
    anchor: cluster.anchor,
  }));

  const totalSalesAmount = copros.reduce((acc, copro) => acc + (copro.salesAmount || 0), 0);

  if (targets.length === 0) {
    return { totalSalesAmount, clusters: [] };
  }

  const clusters = buildClusters(targets, totalSalesAmount);

  if (allowAnchorsFallback) {
    fallbackClusterCoordinates(clusters, copros);
  }

  propagateAssignments(clusters, copros);

  return {
    totalSalesAmount,
    clusters: computeCoverage(clusters, totalSalesAmount),
  };
}


